/**
 * useUTM — captures UTM parameters from the landing URL and persists them
 * for the full session so every form submission can attribute itself to the
 * campaign that drove it.
 *
 * Lifecycle:
 *   1. On page load, read `?utm_source=…&utm_medium=…&utm_campaign=…&utm_content=…&utm_term=…`
 *      from the current URL. If any UTM param is present, write all 5 (plus
 *      `landing_page`) to localStorage under `bdu_utm_v1`. If none are present,
 *      preserve whatever was stored earlier — a visitor who arrives via IG and
 *      then clicks around the site keeps their IG attribution.
 *   2. Expose the current UTM payload via `useUTM()` so forms can spread it
 *      into their POST body.
 *   3. `clearUTM()` is available for explicit resets (e.g. after a conversion
 *      the admin wants to attribute only once — currently we never auto-clear).
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "bdu_utm_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readFromUrl() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    let found = false;
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) {
        out[k] = v.slice(0, 120);
        found = true;
      }
    }
    if (found) {
      out.landing_page = (window.location.origin + window.location.pathname).slice(0, 200);
      out._captured_at = new Date().toISOString();
    }
    return out;
  } catch {
    return {};
  }
}

function readFromStorage() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function writeToStorage(data) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage disabled — fall back to in-memory only */
  }
}

export default function useUTM() {
  const [utm, setUtm] = useState(() => {
    const url = readFromUrl();
    if (Object.keys(url).length > 0) {
      writeToStorage(url);
      return url;
    }
    return readFromStorage();
  });

  useEffect(() => {
    const url = readFromUrl();
    if (Object.keys(url).length > 0) {
      writeToStorage(url);
      setUtm(url);
    }
  }, []);

  return utm;
}

/** Return just the attribution subset that forms should POST — never leak
 *  internal metadata (`_captured_at`) to the API.
 */
export function utmPayload(utm) {
  const out = {};
  for (const k of UTM_KEYS) {
    if (utm && utm[k]) out[k] = utm[k];
  }
  if (utm && utm.landing_page) out.landing_page = utm.landing_page;
  return out;
}

export function clearUTM() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
